import type React from 'react';
import { useSelector } from 'react-redux';
import { yieldCallback } from '@zillow/yield-callback';
import type { RootReduxState } from 'app/shared/flux/reducers';

// Hooks
import useBreadcrumbs from 'app/shared/modules/BreadcrumbNav/useBreadcrumbs';

// Components
import Linker from 'app/shared/modules/Linker';

// Styles
import {
  breadcrumbLinkerClass,
  BreadcrumbNavContainer,
  BreadcrumbNavOrderedList,
  ListItem,
} from './BreadcrumbNav.styles';

interface BreadcrumbNavProps {
  identifier?: string;
  /**
   * Breadcrumb variant:
   * - 'default': Preserves the original search slug and property type text (e.g., "Venice Condos")
   * - 'baseRental': Normalizes all links to base rental paths and uses "rentals" text (e.g., "Venice Listings")
   */
  variant?: 'default' | 'baseRental';
}

const BreadcrumbNav: React.FC<BreadcrumbNavProps> = ({ identifier = 'default', variant = 'default' }) => {
  const slug = useSelector((state: RootReduxState) => state.filter.search.slug);
  const areaCrumbs = useSelector((state: RootReduxState) => state.area?.area?.breadcrumbs);
  const currentListingCrumbs = useSelector(
    (state: RootReduxState) => state.currentListingDetails?.currentListing?.areas?.breadcrumbs,
  );
  const [crumbs] = useBreadcrumbs(areaCrumbs, currentListingCrumbs, slug, variant);

  const yieldTransitionTo = yieldCallback((crumbPath: string): void => {
    window.router.transitionTo(crumbPath);
  });

  const handleCrumbClick = (e: React.MouseEvent<HTMLAnchorElement>, crumbPath: string): void => {
    // Prevent the browser from going through with its default <a> behavior
    e.preventDefault();

    yieldTransitionTo(crumbPath);
  };

  return (
    <BreadcrumbNavContainer aria-label={`breadcrumbs-${identifier}`}>
      <BreadcrumbNavOrderedList>
        {crumbs &&
          crumbs.map((crumb, idx) => {
            const currentPage = idx === crumbs.length - 1;
            const crumbPath = crumb.uri;

            return (
              <ListItem key={`breadcrumb-${crumb.name}`}>
                <Linker
                  to={crumbPath}
                  onClick={(e: React.MouseEvent<HTMLAnchorElement>) => handleCrumbClick(e, crumbPath)}
                  linkType="accent"
                  tabIndex={0}
                  aria-label={`Link to ${crumb.aria ? crumb.aria : crumb.name} page`}
                  aria-current={currentPage ? 'page' : null}
                  className={breadcrumbLinkerClass}
                >
                  {crumb.name}
                </Linker>
              </ListItem>
            );
          })}
      </BreadcrumbNavOrderedList>
    </BreadcrumbNavContainer>
  );
};

export default BreadcrumbNav;
